import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
    OrderDto,
    OrderStatus,
    IOrderDto,
    ReceiptDto,
    WaybillDto,
    GoodsDto,
    ClientDto,
    SiteDto,
    ArticleDto,
    OrderGoodsItemDto,
    OrderDataDto,
    PurchaseOrderDto,
    GoodsStatus,
    BatchDto,
    SpecializedOrderStatus,
    ChangeOrderStatusDto,
    ChangeDemandOrderStatusDto,
    ChangeReturnOrderStatusDto,
    DemandOrderDto,
    ReturnOrderDto,
} from '../../api/ClientApi';
import { RootState } from '../reducers';
import { myAssert } from '../../common/console';
import { WarehouseClient, cancelOrderStep, rejectOrderStep } from '../../api/Extensions';
import { BareGoodsEntity } from '../reducers/goodsToOrderFormReducer';
import { filterUniqueBareGoodsByArticle } from './helper';
import { addOrUpdateMultipleReduxGoods } from './goodsActions';
import { addOrUpdateDemandOrder } from './demandOrderActions';
import { addOrUpdateReturnOrder } from './returnOrderActions';
import { addOrUpdatePurchaseOrder } from './purchaseOrderActions';

export const createNewEmptyOrder = createAction('order/createNewEmptyOrder');
export const createNewEmptyReceptionOrder = createAction('order/createNewEmptyReceptionOrder');
export const clearOrder = createAction('order/clearOrder');

export const addOrderGoodsId = createAction<number>('order/addOrderGoodsId');
export const removeOrderGoodsId = createAction<number>('order/removeOrderGoodsId');
export const setSupplier = createAction<ClientDto>('order/setSupplier');
export const removeSupplier = createAction('order/removeSupplier');
export const setOriginSite = createAction<SiteDto | undefined>('order/setOriginSite');
export const removeOriginSite = createAction('order/removeOriginSite');
export const setDestinationSite = createAction<SiteDto | undefined>('order/setDestinationSite');
export const removeDestinationSite = createAction('order/removeDestinationSite');
export const setOrderStatus = createAction<OrderStatus | undefined>('order/setOrderStatus');
export const updateOrderDescription = createAction<string | undefined>('order/updateOrderDescription');
export const updateOrderData = createAction<string | undefined>('order/updateOrderData');

export const createNewOrder = createAction<IOrderDto>('order/createNewOrder');
export const addOrUpdateOrder = createAction<OrderDto>('order/addOrUpdateOrder');
export const removeOrderFromRedux = createAction<number>('order/removeOrderFromRedux');

export const createNewEmptyBareGoodsEntity = createAction('order/createNewEmptyBareGoodsEntity');
export const clearBareGoods = createAction('order/clearBareGoods');
export const updateBareGoodsArticle = createAction<{ index: number; article: ArticleDto | undefined }>(
    'order/updateBareGoodsArticle',
);
export const updateBareGoodsAvailableQuantity = createAction<{ index: number; availableQuantity: number }>(
    'order/updateBareGoodsAvailableQuantity',
);
export const updateBareGoodsOrderedQuantity = createAction<{ index: number; orderedQuantity: number }>(
    'order/updateBareGoodsOrderedQuantity',
);
export const removeBareGoodsEntity = createAction<number>('order/removeBareGoodsEntity');
export const setBareGoodsGoodsList = createAction<{ index: number; bareGoods?: GoodsDto[] | undefined }>(
    'order/setBareGoodsGoodsList',
);
export const addNewBareGoodsEntity = createAction<BareGoodsEntity>('order/addNewBareGoodsEntity');
export const setBareGoodsEntities = createAction<BareGoodsEntity[]>('order/setBareGoodsEntities');

export const addOrderGoodsItemToOrder = createAction<{ orderId: number; orderGoodsItem?: OrderGoodsItemDto }>(
    'order/addOrderGoodsItemToOrder',
);
export const removeOrderGoodsItemFromOrder = createAction<{ orderId: number; orderGoodsItemId?: number }>(
    'order/removeOrderGoodsItemFromOrder',
);

export const saveOrder = createAsyncThunk<OrderDto, void>(
    'order/saveOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const orderState = rootState.orderForm;

        const order = orderState.order;
        const bareGoods = rootState.goodsToOrderForm.bareGoodsEntities;

        myAssert(() => order !== null);

        if (order === null) {
            throw new Error();
        }

        const orderId: number | undefined = order.id;
        let savedOrder: OrderDto | undefined;

        const newOrder = new OrderDto(order);

        if (!!newOrder.supplierId) {
            newOrder.orderStatus = OrderStatus.Transport;
        }

        const filteredBareGoods = filterUniqueBareGoodsByArticle(bareGoods ?? []);

        if (
            order.orderStatus !== OrderStatus.Idle &&
            order.orderStatus !== OrderStatus.Pending &&
            order.orderStatus !== OrderStatus.Approved
        ) {
            throw new Error();
        }

        if (!orderId || orderId <= 0) {
            const addOrderPromise = WarehouseClient.addNewOrder(newOrder as OrderDto).then(async (resOrder) => {
                let savedOrderGoodsItems: OrderGoodsItemDto[] = [];
                const savedOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
                filteredBareGoods.map((bg) => {
                    const newOrderGood = new OrderGoodsItemDto();
                    newOrderGood.articleId = bg.article?.id;
                    newOrderGood.article = bg.article;
                    newOrderGood.order = resOrder;
                    newOrderGood.orderId = resOrder?.id;
                    newOrderGood.quantity = bg.orderedQuantity;

                    const addOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForOrder(
                        resOrder?.id ?? 0,
                        newOrderGood,
                    );

                    savedOrderGoodsItemsPromises.push(addOrderGoodsItemPromise);
                });

                savedOrderGoodsItems = await Promise.all(savedOrderGoodsItemsPromises);

                if (!!resOrder.demandOrderId) {
                    if (resOrder.demandOrder?.demandOrderStatus === SpecializedOrderStatus.Created) {
                        const cDemandOrderStatus = new ChangeDemandOrderStatusDto({
                            newOrderStatus: SpecializedOrderStatus.InProgress,
                            order: resOrder.demandOrder,
                        });
                        WarehouseClient.changeDemandOrderStatus(cDemandOrderStatus).then((res) =>
                            thunkAPI.dispatch(addOrUpdateDemandOrder(res)),
                        );
                    } else {
                        WarehouseClient.getDemandOrder(resOrder.demandOrderId).then((res) =>
                            thunkAPI.dispatch(addOrUpdateDemandOrder(res)),
                        );
                    }
                }

                if (!!resOrder.returnOrderId) {
                    if (resOrder.returnOrder?.returnOrderStatus === SpecializedOrderStatus.Created) {
                        const cReturnOrderStatus = new ChangeReturnOrderStatusDto({
                            newOrderStatus: SpecializedOrderStatus.InProgress,
                            order: resOrder.returnOrder,
                        });
                        WarehouseClient.changeReturnOrderStatus(cReturnOrderStatus).then((res) =>
                            thunkAPI.dispatch(addOrUpdateReturnOrder(res)),
                        );
                    } else {
                        WarehouseClient.getReturnOrder(resOrder.returnOrderId).then((res) =>
                            thunkAPI.dispatch(addOrUpdateReturnOrder(res)),
                        );
                    }
                }

                resOrder.orderGoodsItems = savedOrderGoodsItems;

                return resOrder;
            });

            savedOrder = await addOrderPromise;
        } else {
            myAssert(() => newOrder.id !== undefined);

            if (newOrder.id === undefined) {
                throw new Error();
            }

            const filteredOrderGoodsItems = newOrder.orderGoodsItems;

            let savedOrderGoodsItems: OrderGoodsItemDto[] = [];
            let removedGoods: number[] = [];

            const savedOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
            const removedOrderGoodsItemsIdsPromises: Promise<number>[] = [];

            // go through oldGoods;
            filteredOrderGoodsItems?.map((fg) => {
                // check if this goods exists in new order
                const orderedGoods = filteredBareGoods?.find((bg) => bg.article?.id === fg.article?.id);

                if (!!orderedGoods) {
                    // if quantity is different, update existing goods quantity
                    if (orderedGoods.orderedQuantity !== fg.quantity) {
                        fg.quantity = orderedGoods.orderedQuantity;

                        const addOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForOrder(orderId, fg).then(
                            (res) => {
                                thunkAPI.dispatch(addOrderGoodsItemToOrder({ orderId, orderGoodsItem: res }));
                                return res;
                            },
                        );

                        savedOrderGoodsItemsPromises.push(addOrderGoodsItemPromise);
                    }
                } else {
                    // this goods does not exist in new order, so we have to delete it and disconnect it from the order
                    if (
                        newOrder.orderStatus === OrderStatus.Idle ||
                        newOrder.orderStatus === OrderStatus.Pending ||
                        newOrder.orderStatus === OrderStatus.Approved
                    ) {
                        const removedOrderGoodsItemsIdsPromise = WarehouseClient.deleteOrderGoodsItem(fg.id ?? 0).then(
                            () => {
                                thunkAPI.dispatch(
                                    removeOrderGoodsItemFromOrder({
                                        orderId: newOrder.id ?? 0,
                                        orderGoodsItemId: fg.id,
                                    }),
                                );
                                return fg.id ?? 0;
                            },
                        );
                        removedOrderGoodsItemsIdsPromises.push(removedOrderGoodsItemsIdsPromise);
                    }
                }
            });

            filteredBareGoods
                .filter((fbg) => !filteredOrderGoodsItems?.find((fg) => fg.article?.id === fbg.article?.id))
                .map((fbg) => {
                    // in this case, goods does not exist in old order, so we have to add id and connect it to the order
                    const newOrderGood = new OrderGoodsItemDto();
                    newOrderGood.articleId = fbg.article?.id;
                    newOrderGood.article = fbg.article;
                    newOrderGood.order = savedOrder;
                    newOrderGood.orderId = savedOrder?.id;
                    newOrderGood.quantity = fbg.orderedQuantity;

                    const addOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForOrder(
                        orderId,
                        newOrderGood,
                    ).then((res) => {
                        thunkAPI.dispatch(addOrderGoodsItemToOrder({ orderId, orderGoodsItem: res }));
                        return res;
                    });

                    savedOrderGoodsItemsPromises.push(addOrderGoodsItemPromise);
                });

            savedOrderGoodsItems = await Promise.all(savedOrderGoodsItemsPromises);

            removedGoods = await Promise.all(removedOrderGoodsItemsIdsPromises);

            newOrder.orderGoodsItems?.push(...savedOrderGoodsItems);
            newOrder.orderGoodsItems = newOrder.orderGoodsItems?.filter((ogi) => !removedGoods.includes(ogi.id ?? 0));

            if (!!newOrder.demandOrderId) {
                WarehouseClient.getDemandOrder(newOrder.demandOrderId).then((res) =>
                    thunkAPI.dispatch(addOrUpdateDemandOrder(res)),
                );
            }

            if (!!newOrder.returnOrderId) {
                WarehouseClient.getReturnOrder(newOrder.returnOrderId).then((res) =>
                    thunkAPI.dispatch(addOrUpdateReturnOrder(res)),
                );
            }

            const addOrderPromise = WarehouseClient.getOrder(orderId, true, true, true);

            savedOrder = await addOrderPromise;
        }

        return savedOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const orderState = (thunkAPI.getState() as RootState).orderForm;

            return orderState.order !== null;
        },
    },
);

export const saveReceptionOrderFromPurchaseOrder = createAsyncThunk<OrderDto, void>(
    'order/saveReceptionOrderFromPurchaseOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const purchaseOrderState = rootState.purchaseOrderForm;

        const purchaseOrder = purchaseOrderState.purchaseOrder;
        const batchArrivalTime = purchaseOrderState.batchArrivalTime;

        const purchaseOrderGoods = rootState.goodsToPurchaseOrderForm.goodBatchEntities;

        myAssert(() => purchaseOrder !== null);

        if (purchaseOrder === null) {
            throw new Error();
        }

        const order = new OrderDto({
            destinationSiteId: purchaseOrder.destinationSiteId,
            purchaseOrderId: purchaseOrder.id,
            supplierId: purchaseOrder.supplierId,
            supplier: purchaseOrder.supplier ?? undefined,
            orderStatus: OrderStatus.Transport,
            orderGoodsItems: [],
            goodsIds: [],
        });

        const addOrderPromise = WarehouseClient.addNewOrder(order).then(async (resOrder) => {
            if (purchaseOrder.purchaseOrderStatus !== SpecializedOrderStatus.InProgress) {
                const newPurhcaseOrder = new PurchaseOrderDto({
                    ...purchaseOrder,
                    goods: [],
                    purchaseOrderStatus: SpecializedOrderStatus.InProgress,
                });

                await WarehouseClient.updatePurchaseOrder(newPurhcaseOrder.id ?? 0, newPurhcaseOrder).then((res) =>
                    thunkAPI.dispatch(addOrUpdatePurchaseOrder(res)),
                );
            }

            let savedOrderGoodsItems: OrderGoodsItemDto[] = [];
            let savedOrderGoods: GoodsDto[] = [];

            const savedOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
            const savedOrderGoodsPromises: Promise<GoodsDto>[] = [];

            purchaseOrderGoods?.map((pog) => {
                const newOrderGoodItem = new OrderGoodsItemDto();
                newOrderGoodItem.articleId = pog.goods.article?.id;
                newOrderGoodItem.article = pog.goods.article;
                newOrderGoodItem.orderId = resOrder?.id;
                newOrderGoodItem.quantity = pog.goods.quantity;

                const addOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForOrder(
                    resOrder?.id ?? 0,
                    newOrderGoodItem,
                );

                savedOrderGoodsItemsPromises.push(addOrderGoodsItemPromise);

                const newBatch = new BatchDto({
                    purchasePricePerArticle: pog.batch.purchasePricePerArticle,
                    uniqueBatchId: pog.batch.uniqueBatchId,
                    batchArrivalTime: batchArrivalTime ?? undefined,
                    supplier: purchaseOrder.supplier ?? undefined,
                    supplierId: purchaseOrder.supplierId,
                });

                const newOrderGood = new GoodsDto();
                newOrderGood.article = pog.goods.article;
                newOrderGood.batch = newBatch;
                newOrderGood.quantity = pog.goods.quantity;
                newOrderGood.goodsStatus = GoodsStatus.InTransport;
                newOrderGood.site = pog.goods.site;
                newOrderGood.siteId = pog.goods.siteId;

                const addOrderGoodsPromise = WarehouseClient.addNewGoodsForOrder(resOrder.id ?? 0, newOrderGood);
                savedOrderGoodsPromises.push(addOrderGoodsPromise);
            });

            savedOrderGoodsItems = await Promise.all(savedOrderGoodsItemsPromises);
            savedOrderGoods = await Promise.all(savedOrderGoodsPromises);

            resOrder.orderGoodsItems = savedOrderGoodsItems;
            resOrder.goodsIds = savedOrderGoods.map((sog) => sog.id ?? []).flat();

            thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(savedOrderGoods));

            return resOrder;
        });

        const savedOrder = await addOrderPromise;

        return savedOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const purchaseOrderState = (thunkAPI.getState() as RootState).purchaseOrderForm;

            return purchaseOrderState.purchaseOrder !== null;
        },
    },
);

export const acceptOrder = createAsyncThunk<OrderDto, void>(
    'order/acceptOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const orderState = rootState.acceptOrderForm;

        const order = orderState.order;

        const oldOrder = new OrderDto();

        oldOrder.init(order);

        myAssert(() => !!oldOrder);

        if (!oldOrder) {
            throw new Error();
        }

        const orderId: number | undefined = oldOrder.id;
        let savedOrder: OrderDto | undefined;

        if (
            oldOrder.orderStatus === OrderStatus.Cancelled ||
            oldOrder.orderStatus === OrderStatus.Returned ||
            oldOrder.orderStatus === OrderStatus.Delivered
        ) {
            throw new Error();
        }

        if (!orderId || orderId <= 0) {
            throw new Error();
        } else {
            myAssert(() => oldOrder.id !== undefined);

            if (oldOrder.id === undefined) {
                throw new Error();
            }

            const changeOrderStatusEnt = new ChangeOrderStatusDto({
                order: oldOrder,
                newOrderStatus: OrderStatus.Pending,
            });
            const addedOrderPromise = WarehouseClient.changeOrderStatus(changeOrderStatusEnt);

            savedOrder = await addedOrderPromise;
        }

        return savedOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const orderState = (thunkAPI.getState() as RootState).acceptOrderForm;

            return orderState.order !== null;
        },
    },
);

export const cancelOrder = createAsyncThunk<OrderDto, void>(
    'order/cancelOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const orderState = rootState.cancelOrderForm;

        const order = orderState.order;

        const oldOrder = new OrderDto();

        oldOrder.init(order);

        myAssert(() => !!oldOrder);

        if (!oldOrder) {
            throw new Error();
        }

        const orderId: number | undefined = oldOrder.id;
        let savedOrder: OrderDto | undefined;

        if (
            oldOrder.orderStatus === OrderStatus.Cancelled ||
            oldOrder.orderStatus === OrderStatus.Returned ||
            oldOrder.orderStatus === OrderStatus.Delivered
        ) {
            throw new Error();
        }

        if (!orderId || orderId <= 0) {
            throw new Error();
        } else {
            myAssert(() => oldOrder.id !== undefined);

            if (oldOrder.id === undefined) {
                throw new Error();
            }

            // One backend transaction: goods in transit back to the origin + status Cancelled.
            const step = await cancelOrderStep(oldOrder.id);

            thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(step.goods));

            savedOrder = step.order;
        }

        return savedOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const orderState = (thunkAPI.getState() as RootState).cancelOrderForm;

            return orderState.order !== null;
        },
    },
);

/**
 * Destination warehouse owner disputes an order in transport ("Nisam primio robu"): goods go back
 * to the origin site and the order becomes Returned. Uses the existing backend transition.
 */
export const rejectOrderDelivery = createAsyncThunk<OrderDto, number>(
    'order/rejectOrderDelivery',
    async (orderId: number, thunkAPI) => {
        const order = await WarehouseClient.getOrder(orderId, true, true, true);

        if (order.orderStatus !== OrderStatus.Transport) {
            throw new Error('Samo nalog u transportu može da se vrati.');
        }

        // One backend transaction: goods back to the origin + status Returned.
        const step = await rejectOrderStep(orderId);

        thunkAPI.dispatch(addOrUpdateMultipleReduxGoods(step.goods));
        thunkAPI.dispatch(addOrUpdateOrder(step.order));

        return step.order;
    },
);

export const cancelDemandOrder = createAsyncThunk<DemandOrderDto, void>(
    'order/cancelDemandOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const orderState = rootState.cancelOrderForm;

        const order = orderState.demandOrder;

        const oldOrder = new DemandOrderDto();

        oldOrder.init(order);

        myAssert(() => !!oldOrder);

        if (!oldOrder) {
            throw new Error();
        }

        const orderId: number | undefined = oldOrder.id;
        let savedOrder: OrderDto | undefined;

        if (
            oldOrder.demandOrderStatus === SpecializedOrderStatus.Cancelled ||
            oldOrder.demandOrderStatus === SpecializedOrderStatus.Closed ||
            oldOrder.demandOrderStatus === SpecializedOrderStatus.Delivered
        ) {
            throw new Error();
        }

        if (!orderId || orderId <= 0) {
            throw new Error();
        } else {
            myAssert(() => oldOrder.id !== undefined);

            if (oldOrder.id === undefined) {
                throw new Error();
            }

            const changeOrderStatusEnt = new ChangeDemandOrderStatusDto({
                order: oldOrder,
                newOrderStatus: SpecializedOrderStatus.Cancelled,
            });
            const addedOrderPromise = WarehouseClient.changeDemandOrderStatus(changeOrderStatusEnt);

            savedOrder = await addedOrderPromise;
        }

        return savedOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const orderState = (thunkAPI.getState() as RootState).cancelOrderForm;

            return orderState.demandOrder !== null;
        },
    },
);

export const cancelReturnOrder = createAsyncThunk<ReturnOrderDto, void>(
    'order/cancelReturnOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const orderState = rootState.cancelOrderForm;

        const order = orderState.returnOrder;

        const oldOrder = new ReturnOrderDto();

        oldOrder.init(order);

        myAssert(() => !!oldOrder);

        if (!oldOrder) {
            throw new Error();
        }

        const orderId: number | undefined = oldOrder.id;
        let savedOrder: OrderDto | undefined;

        if (
            oldOrder.returnOrderStatus === SpecializedOrderStatus.Cancelled ||
            oldOrder.returnOrderStatus === SpecializedOrderStatus.Closed ||
            oldOrder.returnOrderStatus === SpecializedOrderStatus.Delivered
        ) {
            throw new Error();
        }

        if (!orderId || orderId <= 0) {
            throw new Error();
        } else {
            myAssert(() => oldOrder.id !== undefined);

            if (oldOrder.id === undefined) {
                throw new Error();
            }

            const changeOrderStatusEnt = new ChangeReturnOrderStatusDto({
                order: oldOrder,
                newOrderStatus: SpecializedOrderStatus.Cancelled,
            });
            const addedOrderPromise = WarehouseClient.changeReturnOrderStatus(changeOrderStatusEnt);

            savedOrder = await addedOrderPromise;
        }

        return savedOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const orderState = (thunkAPI.getState() as RootState).cancelOrderForm;

            return orderState.returnOrder !== null;
        },
    },
);

export const closeDemandOrder = createAsyncThunk<DemandOrderDto, void>(
    'order/closeDemandOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const orderState = rootState.closeOrderForm;

        const order = orderState.demandOrder;

        const oldOrder = new DemandOrderDto();

        oldOrder.init(order);

        myAssert(() => !!oldOrder);

        if (!oldOrder) {
            throw new Error();
        }

        const orderId: number | undefined = oldOrder.id;
        let savedOrder: OrderDto | undefined;

        if (
            oldOrder.demandOrderStatus === SpecializedOrderStatus.Cancelled ||
            oldOrder.demandOrderStatus === SpecializedOrderStatus.Closed ||
            oldOrder.demandOrderStatus === SpecializedOrderStatus.Delivered
        ) {
            throw new Error();
        }

        if (!orderId || orderId <= 0) {
            throw new Error();
        } else {
            myAssert(() => oldOrder.id !== undefined);

            if (oldOrder.id === undefined) {
                throw new Error();
            }

            const changeOrderStatusEnt = new ChangeDemandOrderStatusDto({
                order: oldOrder,
                newOrderStatus: SpecializedOrderStatus.Closed,
            });
            const addedOrderPromise = WarehouseClient.changeDemandOrderStatus(changeOrderStatusEnt);

            savedOrder = await addedOrderPromise;
        }

        return savedOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const orderState = (thunkAPI.getState() as RootState).closeOrderForm;

            return orderState.demandOrder !== null;
        },
    },
);

export const closeReturnOrder = createAsyncThunk<ReturnOrderDto, void>(
    'order/closeReturnOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const orderState = rootState.closeOrderForm;

        const order = orderState.returnOrder;

        const oldOrder = new ReturnOrderDto();

        oldOrder.init(order);

        myAssert(() => !!oldOrder);

        if (!oldOrder) {
            throw new Error();
        }

        const orderId: number | undefined = oldOrder.id;
        let savedOrder: OrderDto | undefined;

        if (
            oldOrder.returnOrderStatus === SpecializedOrderStatus.Cancelled ||
            oldOrder.returnOrderStatus === SpecializedOrderStatus.Closed ||
            oldOrder.returnOrderStatus === SpecializedOrderStatus.Delivered
        ) {
            throw new Error();
        }

        if (!orderId || orderId <= 0) {
            throw new Error();
        } else {
            myAssert(() => oldOrder.id !== undefined);

            if (oldOrder.id === undefined) {
                throw new Error();
            }

            const changeOrderStatusEnt = new ChangeReturnOrderStatusDto({
                order: oldOrder,
                newOrderStatus: SpecializedOrderStatus.Closed,
            });
            const addedOrderPromise = WarehouseClient.changeReturnOrderStatus(changeOrderStatusEnt);

            savedOrder = await addedOrderPromise;
        }

        return savedOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const orderState = (thunkAPI.getState() as RootState).closeOrderForm;

            return orderState.returnOrder !== null;
        },
    },
);

export const importOrders = createAsyncThunk<OrderDataDto>(
    'order/importOrders',
    async (thunkAPI) => {
        return WarehouseClient.getAllOrders(true, true, true);
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const ordersState = (thunkAPI.getState() as RootState).orders;

            return !ordersState.ordersFetchActive;
        },
    },
);

export const refreshOrdersData = createAsyncThunk<void, void>(
    'order/refreshOrdersData',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const ordersState = rootState.orders;

        return WarehouseClient.getOrdersDataRefreshInfo().then((info) => {
            if (info.infoDate && info.infoDate >= ordersState!.ordersInfoDate!) {
                thunkAPI.dispatch(importOrders());
            }
        });
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const ordersState = (thunkAPI.getState() as RootState).orders;

            if (ordersState.ordersInfoDate === null) {
                return false;
            }

            const orderInfoDate: Date = new Date(ordersState.ordersInfoDate);
            orderInfoDate.setMinutes(orderInfoDate.getMinutes() + 1);

            return orderInfoDate < new Date();
        },
    },
);

export const importOrder = createAsyncThunk<OrderDto, number>('order/importOrder', async (payload, thunkAPI) => {
    return WarehouseClient.getOrder(payload, true, true, true);
});

export const importOrderForCancelation = createAsyncThunk<OrderDto, number>(
    'order/importOrderForCancelation',
    async (payload, thunkAPI) => {
        return WarehouseClient.getOrder(payload, true, true, true);
    },
);

export const importOrderForAcception = createAsyncThunk<OrderDto, number>(
    'order/importOrderForAcception',
    async (payload, thunkAPI) => {
        return WarehouseClient.getOrder(payload, true, true, true);
    },
);

export const importDemandOrderForCancelation = createAsyncThunk<DemandOrderDto, number>(
    'order/importDemandOrderForCancelation',
    async (payload, thunkAPI) => {
        return WarehouseClient.getDemandOrder(payload);
    },
);

export const importReturnOrderForCancelation = createAsyncThunk<ReturnOrderDto, number>(
    'order/importReturnOrderForCancelation',
    async (payload, thunkAPI) => {
        return WarehouseClient.getReturnOrder(payload);
    },
);

export const importDemandOrderForClosing = createAsyncThunk<DemandOrderDto, number>(
    'order/importDemandOrderForClosing',
    async (payload, thunkAPI) => {
        return WarehouseClient.getDemandOrder(payload);
    },
);

export const importReturnOrderForClosing = createAsyncThunk<ReturnOrderDto, number>(
    'order/importReturnOrderForClosing',
    async (payload, thunkAPI) => {
        return WarehouseClient.getReturnOrder(payload);
    },
);

export const importDemandOrderForOrderDetails = createAsyncThunk<DemandOrderDto, number>(
    'order/importDemandOrderForOrderDetails',
    async (payload, thunkAPI) => {
        return WarehouseClient.getDemandOrder(payload);
    },
);

export const importReturnOrderForOrderDetails = createAsyncThunk<ReturnOrderDto, number>(
    'order/importReturnOrderForOrderDetails',
    async (payload, thunkAPI) => {
        return WarehouseClient.getReturnOrder(payload);
    },
);

export const clearOrderForOrderDetails = createAction('order/clearOrderForOrderDetails');

export const updateDemandOrderForOrderDetails = createAction<DemandOrderDto>('order/updateDemandOrderForOrderDetails');

export const updateReturnOrderForOrderDetails = createAction<ReturnOrderDto>('order/updateReturnOrderForOrderDetails');

export const removeDemandOrderForOrderDetails = createAction('order/removeDemandOrderForOrderDetails');

export const removeReturnOrderForOrderDetails = createAction('order/removeReturnOrderForOrderDetails');

export const clearOrderForOrderCancelation = createAction('order/clearOrderForOrderCancelation');
export const clearOrderForOrderAcception = createAction('order/clearOrderForOrderAcception');

export const clearOrderForOrderClosing = createAction('order/clearOrderForOrderClosing');
